"use client";

import clsx from "clsx";
import React, { useState, useEffect } from "react";
import { getCookie, setCookie } from "@repo/client-cookies";
import { Link } from "@repo/next-utils/router";
import { Box } from "@repo/ui/box";
import { Root as Container } from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import { Toast, ToastProvider, ToastViewport } from "@repo/ui/toast";
import { useFloorContext } from "@/components/floor-context/floor-context";
import {
  TOPSHOP_INSTAGRAM_URL,
  TOPMAN_INSTAGRAM_URL,
  TIKTOK_URL,
  PINTEREST_URL,
  CORPORATE_RESPONSIBILITY_URL,
} from "@/constants";
import { CloseIcon } from "@/theme/icons/close";
import {
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY2,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_LINK2_CAPS,
} from "@/theme/topshop/typography/typography-constants";
import { getCustomerGeoCountry } from "@/utils/get-customer-geo-country";
import { Logo } from "../logo";
import * as Viewport from "../viewport";
import classes from "./footer.module.css";

const ONE_WEEK_IN_MINUTES = 10080;

const INSTAGRAM_URLS = {
  topshop: TOPSHOP_INSTAGRAM_URL,
  topman: TOPMAN_INSTAGRAM_URL,
} as const;

export const Footer = () => {
  const [showToast, setShowToast] = useState(false);
  const { floor } = useFloorContext();

  const instagramUrl = floor ? INSTAGRAM_URLS[floor] : TOPSHOP_INSTAGRAM_URL;

  const country = getCustomerGeoCountry();

  const handleToastClose = () => {
    setCookie("nonUkNotificationDismissed", "true", {
      expiresInMinutes: ONE_WEEK_IN_MINUTES,
    });

    setShowToast(false);
  };

  useEffect(() => {
    const nonUkNotificationDismissedCookie = getCookie(
      "nonUkNotificationDismissed",
    );

    if (country === "GB" || nonUkNotificationDismissedCookie) {
      setShowToast(false);
    } else {
      setShowToast(true);
    }
  }, [country]);

  const getAsosRedirectLinks = () => {
    const baseUrl = `https://www.asos.com/${country.toLowerCase()}`;
    return {
      topshop: `${baseUrl}/topshop/`,
      topman: `${baseUrl}/topman/`,
    };
  };

  const redirectLinks = getAsosRedirectLinks();

  return (
    <>
      <Viewport.Large>
        <footer className={classes.footerContainer} role="contentinfo">
          <Container size="large" gutter="2xl">
            <Flex.Container
              gap="xl"
              justify="start"
              className={classes.mainContent}
              asChild
            >
              <nav aria-label="Footer navigation">
                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="brand-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>THE BRAND</h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/about"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              ABOUT
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/press"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              PRESS
                            </Link>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="social-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>FOLLOW US</h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <a
                              href={instagramUrl}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Follow us on Instagram (opens in a new tab)"
                              suppressHydrationWarning={true}
                            >
                              INSTAGRAM
                            </a>
                          </li>
                          <li>
                            <a
                              href={TIKTOK_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Follow us on TikTok (opens in a new tab)"
                            >
                              TIKTOK
                            </a>
                          </li>
                          <li>
                            <a
                              href={PINTEREST_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Follow us on Pinterest (opens in a new tab)"
                            >
                              PINTEREST
                            </a>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="help-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>HELP</h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/delivery-and-returns"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              DELIVERY & RETURNS
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/customer-care"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              CUSTOMER CARE
                            </Link>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="policies-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>POLICIES</h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/content/data-protection"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              DATA PROTECTION
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/content/compliance"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              COMPLIANCE
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/content/cyber-security"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              CYBERSECURITY
                            </Link>
                          </li>
                          <li>
                            <a
                              href={CORPORATE_RESPONSIBILITY_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Corporate Responsibility (opens in a new tab)"
                            >
                              CORPORATE RESPONSIBILITY
                            </a>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>
              </nav>
            </Flex.Container>

            <Box paddingY="3xl" asChild>
              <Flex.Container
                justify="space-between"
                aria-hidden="true"
                columnGap="3xl"
              >
                <Logo variant="topshop" />
                <Logo variant="topman" />
              </Flex.Container>
            </Box>
          </Container>

          <Box
            backgroundColor="secondary"
            className={classes.bottomFooter}
            paddingX="3xl"
            asChild
          >
            <Flex.Container justify="space-between" align="center">
              <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                &copy; {new Date().getFullYear()} TOPSHOP
              </p>
              <Flex.Container align="center" gap="xs">
                <Link
                  href="/content/privacy-policy"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_LABEL3}`}
                  aria-label="Privacy Policy (opens in a new tab)"
                >
                  Privacy & Cookies
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/terms-and-conditions"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_BODY2}`}
                  aria-label="Terms & Conditions (opens in a new tab)"
                >
                  Terms & Conditions
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/content/accessibility"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_BODY2}`}
                  aria-label="Accessibility (opens in a new tab)"
                >
                  Accessibility
                </Link>
              </Flex.Container>
            </Flex.Container>
          </Box>
        </footer>
      </Viewport.Large>

      <Viewport.Small>
        <footer className={classes.viewportSmall} role="contentinfo">
          <Box paddingX="lg" asChild>
            <Flex.Container
              column
              gap="lg"
              className={classes.smallViewportLayout}
            >
              <Box paddingY="3xl" asChild>
                <Flex.Container
                  column
                  align="center"
                  justify="space-between"
                  gap="md"
                >
                  <Logo variant="topshop" width="250px" height="36px" />
                  <Logo variant="topman" width="225px" height="36px" />
                </Flex.Container>
              </Box>
            </Flex.Container>
          </Box>

          <Box
            backgroundColor="secondary"
            paddingY="xs"
            paddingX="xl"
            className={classes.bottomFooter}
            asChild
          >
            <Flex.Container align="center" gap="md" justify="space-between">
              <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                &copy; {new Date().getFullYear()} Topshop
              </p>
              <Flex.Container align="center" wrap gap="xs">
                <Link
                  href="/content/privacy-policy"
                  target="_blank"
                  className={TYPOGRAPHY_BODY3_SENTENCE}
                  aria-label="Privacy Policy (opens in a new tab)"
                >
                  Privacy & Cookies
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/terms-and-conditions"
                  target="_blank"
                  className={TYPOGRAPHY_BODY3_SENTENCE}
                  aria-label="Terms & Conditions (opens in a new tab)"
                >
                  Ts&Cs
                </Link>
              </Flex.Container>
            </Flex.Container>
          </Box>
        </footer>
      </Viewport.Small>

      <ToastProvider>
        <Toast
          variant="info"
          title="You're browsing from outside of UK"
          message={
            <span>
              We currently only ship to the UK. Head to asos.com to shop{" "}
              <a
                className={TYPOGRAPHY_LINK2_CAPS}
                target="_blank"
                href={redirectLinks.topshop}
                rel="noopener noreferrer"
                aria-label="Shop Topshop on ASOS (opens in a new tab)"
              >
                TOPSHOP
              </a>{" "}
              and{" "}
              <a
                className={TYPOGRAPHY_LINK2_CAPS}
                target="_blank"
                href={redirectLinks.topman}
                rel="noopener noreferrer"
                aria-label="Shop Topman on ASOS (opens in a new tab)"
              >
                TOPMAN
              </a>
              .
            </span>
          }
          toastTitle={`${classes.toastTitle} ${TYPOGRAPHY_HEADING3_BOLD}`}
          toastMessage={TYPOGRAPHY_BODY3_SENTENCE}
          dismissLabel="Dismiss UK shipping notification"
          closeIcon={<CloseIcon />}
          onClose={() => handleToastClose()}
          open={showToast}
          duration={180000}
        />
        <ToastViewport />
      </ToastProvider>
    </>
  );
};
